import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { LogError, LogStatus, Metadata, UserInfo } from '@memberjunction/core';
import { environment } from '../../environments/environment';
import { DataFetchService } from './data-fetch.service';
import { EntityNames, QueryNames } from '../utils/constants';
import { MembershipRosterEntity, ExtendedUserEntity } from 'mj_generatedentities';
import { SharedData } from '../utils/shared-data';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class UserAuthService {
  private personIdSubject = new BehaviorSubject<string | null>(null);
  private auraUniqueIdSubject = new BehaviorSubject<string | null>(null);
  private userRoleSubject = new BehaviorSubject<string | null>(null);

  public personId$ = this.personIdSubject.asObservable();
  public auraUniqueId$ = this.auraUniqueIdSubject.asObservable();
  public userRole$ = this.userRoleSubject.asObservable();

  // Cache for role lookups to prevent repeated database queries
  private roleCache = new Map<string, { role: string | null; timestamp: number }>();
  private readonly ROLE_CACHE_TTL = 60000; // 1 minute cache TTL

  constructor(
    private http: HttpClient,
    private dataFetchService: DataFetchService,
    private sharedData: SharedData,
    private userService: UserService
  ) {}

  /**
   * Store user authentication information from claims and ensure ExtendedUsers record exists
   * Now async to support blocking ExtendedUsers record creation
   */
  async setUserAuthInfo(personId: string | number, auraUniqueId: string, role?: string): Promise<void> {
    // Convert personId to string if it's a number (from claims)
    const personIdStr = String(personId);

    this.personIdSubject.next(personIdStr);
    this.auraUniqueIdSubject.next(auraUniqueId);

    // Store role if provided from claims
    if (role) {
      this.userRoleSubject.next(role);
      // Store role mapping in SharedData for role ID lookup
      this.sharedData.setCurrentUserRole(role);
    }

    LogStatus(`UserAuthService: Stored personId=${personIdStr}, auraUniqueId=${auraUniqueId}, role=${role || 'not provided'}`);

    // ⭐ CHANGED: Make ExtendedUsers record creation SYNCHRONOUS (blocking)
    try {
      LogStatus('UserAuthService: Ensuring ExtendedUsers record exists...');
      await this.ensureExtendedUserRecord(personIdStr, auraUniqueId);
      LogStatus('UserAuthService: ✅ ExtendedUsers record ensured successfully');
    } catch (error) {
      // Log error but don't throw - we can still proceed with role from claims
      LogError('UserAuthService: ⚠️ Failed to ensure ExtendedUsers record (non-critical):', undefined, error);
    }
  }

  /**
   * Ensure ExtendedUsers record exists for the user, create if not exists, update if changed
   */
  private async ensureExtendedUserRecord(personId: string, auraUniqueId: string): Promise<void> {
    try {
      const currentUser = this.userService.getUserInfo();
      if (!currentUser || !currentUser.ID) {
        LogError('UserAuthService: Cannot create/update ExtendedUsers record - no current user info available');
        return;
      }

      const userId = currentUser.ID;
      LogStatus(`UserAuthService: Checking for ExtendedUsers record for User_ID: ${userId}`);

      // Fetch role from GET_ROLE_API
      let userRole: string | null = null;
      try {
        userRole = await this.fetchUserRole(personId, auraUniqueId);
        LogStatus(`UserAuthService: Fetched role from API: ${userRole}`);
      } catch (error) {
        LogError('UserAuthService: Failed to fetch role from API, will use null', undefined, error);
      }

      // Check if ExtendedUsers record already exists
      const existingRecords = await this.dataFetchService.fetchEntityData<ExtendedUserEntity>(
        EntityNames.ExtendedUsers,
        `User_ID = '${userId}'`,
        '',
        'entity_object'
      );

      let extendedUser: ExtendedUserEntity;

      if (existingRecords.length > 0) {
        // Record exists - update it
        extendedUser = existingRecords[0];
        LogStatus(`UserAuthService: ExtendedUsers record found (ID: ${extendedUser.ID}), updating fields...`);

        // Update fields (only if changed)
        extendedUser.PersonID = personId;
        extendedUser.AuraUniqueID = auraUniqueId;
        extendedUser.Role = userRole;

        // Check if entity is dirty before saving
        if (extendedUser.Dirty) {
          LogStatus(`UserAuthService: ExtendedUsers record has changes, saving...`);
          const success = await extendedUser.Save();

          if (!success) {
            throw new Error(`Failed to update ExtendedUsers record: ${extendedUser.LatestResult?.Message || 'Unknown error'}`);
          }

          LogStatus(`UserAuthService: Successfully updated ExtendedUsers record (ID: ${extendedUser.ID}) with role: ${userRole}`);
        } else {
          LogStatus(`UserAuthService: ExtendedUsers record unchanged, no save needed`);
        }

      } else {
        // Record doesn't exist - create new one
        LogStatus(`UserAuthService: No ExtendedUsers record found, creating new record...`);

        // Create new ExtendedUsers entity
        const md = new Metadata();
        extendedUser = await md.GetEntityObject<ExtendedUserEntity>(EntityNames.ExtendedUsers);

        if (!extendedUser) {
          throw new Error('Failed to create ExtendedUserEntity object');
        }

        // Populate fields
        extendedUser.User_ID = userId;
        extendedUser.PersonID = personId;
        extendedUser.AuraUniqueID = auraUniqueId;
        extendedUser.Role = userRole;

        // Save entity
        const success = await extendedUser.Save();

        if (!success) {
          throw new Error(`Failed to save ExtendedUsers record: ${extendedUser.LatestResult?.Message || 'Unknown error'}`);
        }

        LogStatus(`UserAuthService: Successfully created ExtendedUsers record (ID: ${extendedUser.ID}) with role: ${userRole}`);
      }

    } catch (error) {
      LogError('UserAuthService: Error ensuring ExtendedUsers record', undefined, error);
      throw error;
    }
  }

  /**
   * Get role for a user by their User_ID - checks ExtendedUsers first, then fallback
   * This is the PREFERRED method when User_ID is available (faster than email lookup)
   *
   * @param userId - MJ User.ID (from __mj.User table)
   * @param email - User email (optional, for fallback if User_ID lookup fails)
   * @param personId - PersonId/MemberID (optional, if already known)
   * @param auraUniqueId - AuraUniqueId (optional, defaults to current user's)
   * @returns Promise resolving to role name or null
   */
  async getRoleByUserId(
    userId: string,
    email?: string,
    personId?: string | null,
    auraUniqueId?: string
  ): Promise<string | null> {
    try {
      // Check cache first
      const cacheKey = `user_${userId}`;
      const cached = this.roleCache.get(cacheKey);
      const now = Date.now();

      if (cached && (now - cached.timestamp) < this.ROLE_CACHE_TTL) {
        LogStatus(`UserAuthService: ✅ Found role in cache for User_ID: ${userId} - ${cached.role}`);
        // Update the current user role subject so getUserRole() returns the correct role
        if (cached.role) {
          this.userRoleSubject.next(cached.role);
          this.sharedData.setCurrentUserRole(cached.role);
        }
        return cached.role;
      }

      LogStatus(`UserAuthService: Getting role for User_ID: ${userId}`);

      // STEP 1: Try ExtendedUsers first (FASTEST - direct User_ID lookup)
      const extendedUsers = await this.dataFetchService.fetchEntityData<ExtendedUserEntity>(
        EntityNames.ExtendedUsers,
        `User_ID = '${userId}'`,
        '',
        'simple'
      );

      if (extendedUsers.length > 0) {
        const role = extendedUsers[0].Role;
        if (role) {
          LogStatus(`UserAuthService: ✅ Found role in ExtendedUsers: ${role}`);
          // Cache the result
          this.roleCache.set(cacheKey, { role, timestamp: now });
          // Update the current user role subject so getUserRole() returns the correct role
          this.userRoleSubject.next(role);
          // Store role mapping in SharedData for role ID lookup
          this.sharedData.setCurrentUserRole(role);
          return role;
        }
        LogStatus(`UserAuthService: ExtendedUsers record exists but Role is null`);
      } else {
        LogStatus(`UserAuthService: No ExtendedUsers record found for User_ID: ${userId}`);
      }

      // STEP 2: Fallback - Get PersonId from MembershipRoster if not provided
      if (!personId) {
        if (!email) {
          LogError(`UserAuthService: Cannot fallback without email or personId`);
          return null;
        }

        LogStatus(`UserAuthService: Falling back to ExtendedUser lookup by ID: ${userId}`);
        personId = await this.getPersonIdFromuserID(userId);

        if (!personId) {
          LogStatus(`UserAuthService: Falling back to MembershipRoster lookup by email: ${email}`);
          personId = await this.getPersonIdFromEmail(email);

          if (!personId) {
            LogStatus(`UserAuthService: No PersonId found in MembershipRoster for email: ${email}`);
            return null;
          }
        }
      }

      // STEP 3: Fallback - Fetch role from GET_ROLE_API
      const auraId = auraUniqueId || this.getAuraUniqueId();
      if (!auraId) {
        LogError(`UserAuthService: No AuraUniqueId available for GET_ROLE_API call`);
        return null;
      }

      LogStatus(`UserAuthService: Falling back to GET_ROLE_API with PersonId: ${personId}`);
      const role = await this.fetchUserRole(personId, auraId);
      LogStatus(`UserAuthService: ✅ Fetched role from GET_ROLE_API (fallback): ${role}`);

      return role;

    } catch (error) {
      LogError('UserAuthService: Error getting role by User_ID', undefined, error);
      return null;
    }
  }

  /**
   * Get role for a user by their email - checks ExtendedUsers first, then fallback
   * NOTE: Prefer getRoleByUserId() when User_ID is available (faster)
   *
   * @param email - User email address
   * @param personId - PersonId/MemberID (optional, if already known)
   * @param auraUniqueId - AuraUniqueId (optional, defaults to current user's)
   * @returns Promise resolving to role name or null
   */
  async getRoleByEmail(
    email: string,
    personId?: string | null,
    auraUniqueId?: string
  ): Promise<string | null> {
    try {
      LogStatus(`UserAuthService: Getting role for email: ${email}`);

      // STEP 1: Try ExtendedUsers via JOIN with __mj.User
      // NOTE: This requires a subquery, so it's slower than User_ID lookup
      const extendedUsersQuery = `
        User_ID IN (
          SELECT ID FROM __mj.[User]
          WHERE Email = '${email.replace(/'/g, "''")}'
        )
      `;

      const extendedUsers = await this.dataFetchService.fetchEntityData<ExtendedUserEntity>(
        EntityNames.ExtendedUsers,
        extendedUsersQuery,
        '',
        'simple'
      );

      if (extendedUsers.length > 0) {
        const role = extendedUsers[0].Role;
        if (role) {
          LogStatus(`UserAuthService: ✅ Found role in ExtendedUsers: ${role}`);
          // Update the current user role subject so getUserRole() returns the correct role
          this.userRoleSubject.next(role);
          this.sharedData.setCurrentUserRole(role);
          return role;
        }
      }

      // STEP 2: Fallback - Get PersonId from MembershipRoster
      if (!personId) {
        LogStatus(`UserAuthService: Falling back to MembershipRoster lookup`);
        personId = await this.getPersonIdFromEmail(email);

        if (!personId) {
          LogStatus(`UserAuthService: No PersonId found for email: ${email}`);
          return null;
        }
      }

      // STEP 3: Fallback - Fetch role from GET_ROLE_API
      const auraId = auraUniqueId || this.getAuraUniqueId();
      if (!auraId) {
        LogError(`UserAuthService: No AuraUniqueId available`);
        return null;
      }

      LogStatus(`UserAuthService: Falling back to GET_ROLE_API`);
      const role = await this.fetchUserRole(personId, auraId);
      LogStatus(`UserAuthService: ✅ Fetched role from GET_ROLE_API: ${role}`);

      return role;

    } catch (error) {
      LogError('UserAuthService: Error getting role by email', undefined, error);
      return null;
    }
  }

  /**
   * Get stored personId
   */
  getPersonId(): string | null {
    return this.personIdSubject.value;
  }

  /**
   * Get stored auraUniqueId
   */
  getAuraUniqueId(): string | null {
    return this.auraUniqueIdSubject.value;
  }

  /**
   * Get current user role
   */
  getUserRole(): string | null {
    return this.userRoleSubject.value;
  }

  /**
   * Call the role API to get user's role, with retry logic and fallback to claims
   * Implements exponential backoff for resilience against transient network failures
   */
  async fetchUserRole(customPersonId?: string, customAuraUniqueId?: string): Promise<string> {
    const personId = customPersonId || this.getPersonId();
    const auraUniqueId = customAuraUniqueId || this.getAuraUniqueId();

    if (!personId || !auraUniqueId) {
      throw new Error('PersonId and AuraUniqueId must be set before fetching user role');
    }

    // Try GET_ROLE_API first if configured
    if (environment.GET_ROLE_API) {
      const maxRetries = 3;
      let lastError: unknown;

      // Retry loop with exponential backoff
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          LogStatus(`UserAuthService: Fetching role from API (attempt ${attempt}/${maxRetries})...`);

          const headers = new HttpHeaders({
            'x-aura-token': auraUniqueId
          });

          const response = await firstValueFrom(this.http.get(
            `${environment.GET_ROLE_API}?personId=${personId}`,
            { headers, responseType: 'text' }
          ));

          let roleName = response as string;

          // Clean up role name - remove extra quotes and trim whitespace
          roleName = roleName.replace(/^["']+|["']+$/g, '').trim();

          // Store the role for future use
          this.userRoleSubject.next(roleName);

          // Store role mapping in SharedData for role ID lookup
          this.sharedData.setCurrentUserRole(roleName);

          LogStatus(`UserAuthService: ✅ Fetched user role from API (attempt ${attempt}): "${roleName}"`);

          return roleName;
        } catch (error) {
          lastError = error;
          LogError(`UserAuthService: ⚠️ Role fetch attempt ${attempt}/${maxRetries} failed:`, undefined, error);

          // If not the last attempt, wait before retrying with exponential backoff
          if (attempt < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt - 1) * 1000;
            LogStatus(`UserAuthService: Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // All retries failed, try fallback to claims
      LogError('UserAuthService: All role fetch attempts failed, trying fallback to claims', undefined, lastError);

      // Fall back to role from claims if API fails
      const claimsRole = this.getUserRole();
      if (claimsRole) {
        LogStatus(`UserAuthService: Using fallback role from claims: ${claimsRole}`);
        return claimsRole;
      }

      // If both API and claims fail, set "No Access" as default
      LogError('UserAuthService: No role available from API or claims, setting No Access');
      this.userRoleSubject.next('No Access');
      return 'No Access';
    }

    // If GET_ROLE_API is not configured, fall back to claims
    const claimsRole = this.getUserRole();
    if (claimsRole) {
      LogStatus(`UserAuthService: GET_ROLE_API not configured, using role from claims: ${claimsRole}`);
      return claimsRole;
    }

    // If neither API is configured nor claims role is available
    throw new Error('GET_ROLE_API environment variable is not configured and no role provided in claims');
  }

  /**
   * Update ONLY personId and auraUniqueId without touching role
   * Used during impersonation when token refreshes but we want to keep impersonated user's role
   */
  updateAuthInfoOnly(personId: string | number, auraUniqueId: string): void {
    const personIdStr = String(personId);
    this.personIdSubject.next(personIdStr);
    this.auraUniqueIdSubject.next(auraUniqueId);
    LogStatus(`UserAuthService: Updated auth info only - personId=${personIdStr}, auraUniqueId=${auraUniqueId} (role unchanged)`);
  }

  /**
   * Clear all stored authentication information
   */
  clearUserAuthInfo(): void {
    this.personIdSubject.next(null);
    this.auraUniqueIdSubject.next(null);
    this.userRoleSubject.next(null);

    // Clear role cache
    this.roleCache.clear();

    // Clear role mapping in SharedData
    this.sharedData.clearCurrentUserRole();

    LogStatus('UserAuthService: Cleared all user auth info');
  }

  /**
   * Clear only the role cache (used when user context changes but auth info persists)
   */
  clearRoleCache(): void {
    this.roleCache.clear();
    LogStatus('UserAuthService: Cleared role cache');
  }

  /**
   * Check if user has access (role is not "No Access")
   */
  hasAccess(): boolean {
    const role = this.getUserRole();
    return role !== null && role !== 'No Access';
  }

  /**
   * Check if user has specific role
   */
  hasRole(roleName: string): boolean {
    return this.getUserRole() === roleName;
  }

  /**
   * Check if user is chapter admin (ChapterAdmin role)
   */
  isChapterAdmin(): boolean {
    return this.hasRole('Chapter Admin');
  }

  /**
   * Check if user is staff
   */
  isStaff(): boolean {
    return this.hasRole('Staff');
  }

  /**
   * Fetch role for impersonated user using their email to find MemberID from MembershipRoster
   * @param impersonatedUserEmail - Email of the impersonated user
   * @returns Promise resolving to user role string or null if not found
   */
  async fetchImpersonatedUserRoleByEmail(impersonatedUserEmail: string): Promise<string | null> {
    try {
      LogStatus(`UserAuthService: Fetching role for impersonated user with email: ${impersonatedUserEmail}`);

      // Get the impersonated user's personId from MembershipRoster using their email
      const impersonatedPersonId = await this.getPersonIdFromEmail(impersonatedUserEmail);

      if (!impersonatedPersonId) {
        LogError(`UserAuthService: No MemberID found in MembershipRoster for email: ${impersonatedUserEmail}`);
        return null;
      }

      // Use the logged-in user's auraUniqueId (for header authentication)
      const auraUniqueId = this.getAuraUniqueId();
      if (!auraUniqueId) {
        LogError('UserAuthService: No auraUniqueId available for impersonated user role fetch');
        return null;
      }

      LogStatus(`UserAuthService: Found MemberID ${impersonatedPersonId} for email ${impersonatedUserEmail}, fetching role...`);

      // Fetch role for the impersonated user using their personId and logged-in user's auraUniqueId
      return await this.fetchUserRole(impersonatedPersonId, auraUniqueId);

    } catch (error) {
      LogError('UserAuthService: Error fetching impersonated user role by email', undefined, error);
      return null;
    }
  }

  /**
   * Get personId (MemberID) from MembershipRoster using email
   * @param email - Email address to search for
   * @returns Promise resolving to MemberID or null if not found
   */
  private async getPersonIdFromEmail(email: string): Promise<string | null> {
    try {
      // Query MembershipRoster to find MemberID for the given email using DataFetchService
      const memberRecords = await this.dataFetchService.fetchEntityData<MembershipRosterEntity>(
        EntityNames.MembershipRosters,
        `Email = '${email.replace(/'/g, "''")}'`, // Escape single quotes for SQL safety
        '', // No specific order by needed
        'simple' // Use simple result type for better performance
      );

      if (memberRecords.length === 0) {
        LogStatus(`UserAuthService: No MembershipRoster record found for email: ${email}`);
        return null;
      }

      if (memberRecords.length > 1) {
        LogStatus(`UserAuthService: Multiple MembershipRoster records found for email: ${email}. Using first match.`);
      }

      const memberRecord = memberRecords[0];
      const memberId = memberRecord.MemberID;

      if (!memberId) {
        LogError(`UserAuthService: MemberID is null/empty for email: ${email}`);
        return null;
      }

      LogStatus(`UserAuthService: Found MemberID ${memberId} for email ${email}`);
      return memberId.toString();

    } catch (error) {
      LogError('UserAuthService: Error querying MembershipRoster for email using DataFetchService', undefined, error);
      return null;
    }
  }

  /**
   * Get personId (MemberID) from ExtendedUser using UserID
   * @param userID - User ID to search for
   * @returns Promise resolving to MemberID or null if not found
   */
  private async getPersonIdFromuserID(userID: string): Promise<string | null> {
    try {
      // Query ExtendedUsers to find MemberID for the given UserID using DataFetchService
      const memberRecords = await this.dataFetchService.fetchEntityData<ExtendedUserEntity>(
        EntityNames.ExtendedUsers,
        `User_ID = '${userID.replace(/'/g, "''")}'`, // Escape single quotes for SQL safety
        '', // No specific order by needed
        'simple' // Use simple result type for better performance
      );

      if (memberRecords.length === 0) {
        LogStatus(`UserAuthService: No ExtendedUsers record found for ID: ${userID}`);
        return null;
      }

      if (memberRecords.length > 1) {
        LogStatus(`UserAuthService: Multiple ExtendedUsers records found for ID: ${userID}. Using first match.`);
      }

      const memberRecord = memberRecords[0];
      const memberId = memberRecord.PersonID;

      if (!memberId) {
        LogError(`UserAuthService: MemberID is null/empty for ID: ${userID}`);
        return null;
      }

      LogStatus(`UserAuthService: Found MemberID ${memberId} for ID ${userID}`);
      return memberId.toString();

    } catch (error) {
      LogError('UserAuthService: Error querying ExtendedUsers for ID using DataFetchService', undefined, error);
      return null;
    }
  }

  /**
   * Gets the committee IDs that a user has access to based on PersonChapterLink
   * @param user - UserInfo object (can be impersonated user's email)
   * @returns Promise resolving to array of committee IDs the user has access to
   */
  public async getUserCommitteeAccess(user: UserInfo): Promise<string[]> {
    try {
      LogStatus(`UserAuthService: Getting committee access for user: ${user.Email}`);

      // First get the MemberID for the user email
      const memberId = await this.getPersonIdFromuserID(user.ID);
      if (!memberId) {
        LogError(`UserAuthService: No MemberID found for email: ${user.Email}`);
        return [];
      }

      // Execute PersonChapterLink query with MemberID and Email parameters
      const queryResult = await this.dataFetchService.executeQuery({
        QueryName: QueryNames.PersonChapterLink,
        Parameters: {
          MemberID: memberId,
          Email: user.Email
        }
      });

      if (!queryResult.success || !queryResult.data) {
        LogError(`UserAuthService: PersonChapterLink query failed: ${queryResult.error}`);
        return [];
      }

      // Extract unique committee IDs from the results
      const committeeIds = (queryResult.data as PersonChapterLinkResult[])
        .map((row) => row.CommitteeID)
        .filter((id, index, array) => array.indexOf(id) === index) // Remove duplicates
        .filter(id => id != null); // Remove null/undefined values

      LogStatus(`UserAuthService: Found ${committeeIds.length} committee access permissions for user ${user.Email}: [${committeeIds.join(', ')}]`);

      return committeeIds;

    } catch (error) {
      LogError('UserAuthService: Error getting user committee access', undefined, error);
      return [];
    }
  }

}

/**
 * Interface for PersonChapterLink query results
 */
export interface PersonChapterLinkResult {
  MemberID: string;
  Email: string;
  CommitteeID: string;
  CommitteeName: string;
  StartDate: string;
  EndDate: string;
  EffectiveDate: Date;
  ExpirationDate: Date;
}